import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Trophy, CreditCard, CheckCircle, Users, Trash, Pencil, User,
  EnvelopeSimple, Phone, ListChecks
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const leagueCategories = [
  { value: 'misto', label: 'Misto' },
  { value: 'open', label: 'Open' },
  { value: 'feminino', label: 'Feminino' },
  { value: 'rei_rainha', label: 'Rei e Rainha' }
];

const statusColors = {
  pendente: 'badge-warning',
  confirmada: 'badge-success',
  cancelada: 'badge-error'
};

export default function Courts() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('inscricao');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Registration form
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', categories: [], notes: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [lastRegId, setLastRegId] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Admin edit
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [duplas, setDuplas] = useState([]);
  const [duplasFilter, setDuplasFilter] = useState('all');
  const [duplaForm, setDuplaForm] = useState({ id: null, category: 'duplas_masculino', player1: '', player2: '', notes: '' });
  const [duplaSaving, setDuplaSaving] = useState(false);

  const fetchDuplas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/league/duplas`);
      setDuplas(data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (user?.role === 'admin') fetchDuplas(); }, [user, fetchDuplas]);

  const submitDupla = async (e) => {
    e?.preventDefault?.();
    if (!duplaForm.player1.trim() || !duplaForm.player2.trim()) {
      toast.error('Preencha os dois nomes da dupla');
      return;
    }
    setDuplaSaving(true);
    try {
      const payload = {
        category: duplaForm.category,
        player1: duplaForm.player1.trim(),
        player2: duplaForm.player2.trim(),
        notes: duplaForm.notes.trim() || null,
      };
      if (duplaForm.id) {
        await axios.put(`${API}/league/duplas/${duplaForm.id}`, payload, { withCredentials: true });
        toast.success('Dupla atualizada');
      } else {
        await axios.post(`${API}/league/duplas`, payload, { withCredentials: true });
        toast.success('Dupla criada');
      }
      setDuplaForm({ id: null, category: duplaForm.category, player1: '', player2: '', notes: '' });
      fetchDuplas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setDuplaSaving(false);
    }
  };

  const editDupla = (d) => {
    setDuplaForm({ id: d.id, category: d.category, player1: d.player1, player2: d.player2, notes: d.notes || '' });
  };

  const deleteDupla = async (id) => {
    if (!window.confirm('Remover esta dupla?')) return;
    try {
      await axios.delete(`${API}/league/duplas/${id}`, { withCredentials: true });
      toast.success('Dupla removida');
      fetchDuplas();
    } catch (err) {
      toast.error('Erro ao remover');
    }
  };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentReg, setCurrentReg] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', payment_status: '', notes: '' });

  const isAdmin = user?.role === 'admin';

  const fetchRegistrations = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/league/registrations`, { withCredentials: true });
      setRegistrations(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, regId, attempts = 0) => {
    if (attempts >= 6) {
      setPollingPayment(false);
      toast.info('Verificação expirou. Entre em contato para confirmar.');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/league/checkout-status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPollingPayment(false);
        setSuccess(true);
        toast.success('Pagamento confirmado! Inscrição realizada com sucesso!');
        setSearchParams({});
        if (isAdmin) fetchRegistrations();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, regId, attempts + 1), 2000);
    } catch {
      if (attempts < 5) setTimeout(() => pollPaymentStatus(sessionId, regId, attempts + 1), 2000);
      else { setPollingPayment(false); }
    }
  }, [isAdmin, fetchRegistrations, setSearchParams]);

  useEffect(() => { fetchRegistrations(); }, [fetchRegistrations]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const regId = searchParams.get('reg_id');
    if (sessionId && regId) {
      setPollingPayment(true);
      pollPaymentStatus(sessionId, regId);
    }
  }, [searchParams, pollPaymentStatus]);

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    if (formData.categories.length === 0) {
      toast.error('Selecione pelo menos uma categoria');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/league/register`, formData);
      setLastRegId(data.id);
      toast.success('Inscrição registrada! Redirecionando para pagamento...');

      // Redirect to Stripe checkout
      setStripeLoading(true);
      const originUrl = window.location.origin;
      const checkoutRes = await axios.post(`${API}/league/checkout`, {
        registration_id: data.id,
        origin_url: originUrl
      });
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar inscrição');
      setSaving(false);
      setStripeLoading(false);
    }
  };

  const handleEdit = (reg) => {
    setCurrentReg(reg);
    setEditForm({ status: reg.status, payment_status: reg.payment_status, notes: reg.notes || '' });
    setEditDialogOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/league/registrations/${currentReg.id}`, editForm, { withCredentials: true });
      toast.success('Inscrição atualizada');
      setEditDialogOpen(false);
      fetchRegistrations();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/league/registrations/${currentReg.id}`, { withCredentials: true });
      toast.success('Inscrição removida');
      setDeleteDialogOpen(false);
      fetchRegistrations();
    } catch { toast.error('Erro ao remover'); }
  };

  const filteredRegs = (() => {
    let list = registrations;
    if (filter !== 'all') list = list.filter(r => r.status === filter);
    if (categoryFilter !== 'all') list = list.filter(r => Array.isArray(r.categories) && r.categories.includes(categoryFilter));
    return list;
  })();
  const getCatLabel = (val) => leagueCategories.find(c => c.value === val)?.label || val;
  const categoryCounts = leagueCategories.reduce((acc, cat) => {
    acc[cat.value] = registrations.filter(r => Array.isArray(r.categories) && r.categories.includes(cat.value)).length;
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="courts-page">
        {/* Header */}
        <div>
          <h1 className="page-title">TK League / Rei e Rainha</h1>
          <p className="text-[#73706A] mt-1">Competições e campeonatos da Tia Karol Sports</p>
        </div>

        {/* Polling Banner */}
        {pollingPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#0077B6] font-medium">Verificando pagamento...</p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F2EB]">
            <TabsTrigger value="inscricao" data-testid="tab-inscricao">Inscreva-se Aqui</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="admin" data-testid="tab-admin-league">
                Inscricoes ({registrations.length})
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="duplas-admin" data-testid="tab-admin-duplas">
                Duplas
              </TabsTrigger>
            )}
          </TabsList>

          {/* Registration Tab */}
          <TabsContent value="inscricao" className="mt-6">
            {success ? (
              <div className="max-w-lg mx-auto text-center bg-white border border-[#E6E2D8] rounded-xl p-8">
                <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-white" weight="fill" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">
                  Inscrição Confirmada!
                </h2>
                <p className="text-[#73706A] mb-6">
                  Sua inscrição na TK League foi confirmada. Nos vemos na quadra!
                </p>
                <Button
                  onClick={() => { setSuccess(false); setFormData({ name: '', email: '', phone: '', categories: [], notes: '' }); }}
                  className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                  data-testid="new-registration-btn"
                >
                  Nova Inscrição
                </Button>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                {/* Hero Banner */}
                <div
                  className="h-48 rounded-xl bg-cover bg-center relative overflow-hidden mb-6"
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541819616892-71c1f5112fa5?q=80&w=2940&auto=format&fit=crop)' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
                  <div className="absolute bottom-0 left-0 p-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy size={28} className="text-[#FFB703]" weight="fill" />
                      <h2 className="font-['Outfit'] text-2xl font-bold">TK League / Rei e Rainha</h2>
                    </div>
                    <p className="text-white/80">Inscreva-se e participe das competições!</p>
                  </div>
                </div>

                {/* Registration Form */}
                <div className="bg-white border border-[#E6E2D8] rounded-xl p-6">
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1A1A1A] mb-6 flex items-center gap-2">
                    <ListChecks size={24} className="text-[#E85D04]" />
                    Formulário de Inscrição
                  </h3>

                  <form onSubmit={handleSubmitRegistration} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label>Nome completo *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Seu nome"
                          required
                          data-testid="league-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="seu@email.com"
                          required
                          data-testid="league-email-input"
                        />
                      </div>
                      <div>
                        <Label>Telefone *</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(00) 00000-0000"
                          required
                          data-testid="league-phone-input"
                        />
                      </div>
                    </div>

                    {/* Category Selection */}
                    <div>
                      <Label className="mb-3 block">Categorias que vai jogar *</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {leagueCategories.map((cat) => (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => toggleCategory(cat.value)}
                            className={`p-3 rounded-lg border text-sm font-medium text-left transition-all ${
                              formData.categories.includes(cat.value)
                                ? 'bg-[#E85D04] text-white border-[#E85D04] shadow-sm'
                                : 'bg-white text-[#1A1A1A] border-[#E6E2D8] hover:border-[#E85D04]'
                            }`}
                            data-testid={`league-cat-${cat.value}`}
                          >
                            <Trophy
                              size={18}
                              className={`mb-1 ${formData.categories.includes(cat.value) ? 'text-white' : 'text-[#E85D04]'}`}
                              weight={formData.categories.includes(cat.value) ? 'fill' : 'regular'}
                            />
                            {cat.label}
                          </button>
                        ))}
                      </div>
                      {formData.categories.length > 0 && (
                        <p className="text-sm text-[#2D6A4F] mt-2">
                          {formData.categories.length} categoria(s) selecionada(s)
                        </p>
                      )}
                    </div>

                    <div>
                      <Label>Observações</Label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="input-field min-h-[80px] resize-none"
                        placeholder="Alguma informação adicional?"
                        data-testid="league-notes-input"
                      />
                    </div>

                    {/* Payment Info */}
                    <div className="p-4 bg-[#F5F2EB] rounded-lg border border-[#E6E2D8]">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard size={20} className="text-[#E85D04]" />
                        <span className="font-medium text-[#1A1A1A]">Taxa de Inscrição</span>
                      </div>
                      <p className="text-2xl font-bold text-[#E85D04] font-['Outfit']">$30.00</p>
                      <p className="text-xs text-[#73706A] mt-1">
                        Pagamento seguro via cartão de crédito/débito (Stripe)
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={saving || stripeLoading || formData.categories.length === 0}
                      className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3 text-base"
                      data-testid="league-submit-btn"
                    >
                      {stripeLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Redirecionando para pagamento...
                        </div>
                      ) : saving ? (
                        'Registrando...'
                      ) : (
                        <span className="flex items-center gap-2 justify-center">
                          <CreditCard size={20} /> Inscrever-se e Pagar $30.00
                        </span>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Admin Tab */}
          {isAdmin && (
            <TabsContent value="admin" className="mt-6 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('all')}>
                  <p className="text-2xl font-bold font-['Outfit'] text-[#1A1A1A]">{registrations.length}</p>
                  <p className="text-sm text-[#73706A]">Total</p>
                </div>
                <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('pendente')}>
                  <p className="text-2xl font-bold font-['Outfit'] text-[#FFB703]">
                    {registrations.filter(r => r.status === 'pendente').length}
                  </p>
                  <p className="text-sm text-[#73706A]">Pendentes</p>
                </div>
                <div className="stat-card text-center cursor-pointer" onClick={() => setFilter('confirmada')}>
                  <p className="text-2xl font-bold font-['Outfit'] text-[#2D6A4F]">
                    {registrations.filter(r => r.status === 'confirmada').length}
                  </p>
                  <p className="text-sm text-[#73706A]">Confirmadas</p>
                </div>
                <div className="stat-card text-center">
                  <p className="text-2xl font-bold font-['Outfit'] text-[#E85D04]">
                    ${(registrations.filter(r => r.payment_status === 'pago').length * 30).toFixed(2)}
                  </p>
                  <p className="text-sm text-[#73706A]">Receita</p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-[#73706A]">Status:</span>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-40" data-testid="league-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pendente">Pendentes</SelectItem>
                    <SelectItem value="confirmada">Confirmadas</SelectItem>
                    <SelectItem value="cancelada">Canceladas</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-[#73706A] ml-2">Categoria:</span>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48" data-testid="league-category-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {leagueCategories.map(c => (
                      <SelectItem key={c.value} value={c.value} data-testid={`league-cat-filter-${c.value}`}>
                        {c.label} ({categoryCounts[c.value] || 0})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categoryFilter !== 'all' && (
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className="text-xs text-[#9B2226] hover:underline"
                    data-testid="clear-category-filter"
                  >
                    Limpar
                  </button>
                )}
                <span className="text-sm text-[#73706A] ml-auto" data-testid="league-results-count">
                  {filteredRegs.length} resultado(s)
                </span>
              </div>

              {/* Registrations List */}
              <div className="space-y-3">
                {loading ? (
                  <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
                    <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : filteredRegs.length === 0 ? (
                  <div className="bg-white border border-[#E6E2D8] rounded-lg p-8 text-center">
                    <Trophy size={48} className="text-[#E6E2D8] mx-auto mb-4" />
                    <p className="text-[#73706A]">Nenhuma inscrição encontrada</p>
                  </div>
                ) : (
                  filteredRegs.map((reg, index) => (
                    <div
                      key={reg.id}
                      className="bg-white border border-[#E6E2D8] rounded-lg p-5 hover:border-[#E85D04] transition-colors"
                      data-testid={`league-reg-${index}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-[#E85D04] rounded-full flex items-center justify-center">
                              <User size={18} className="text-white" />
                            </div>
                            <div>
                              <h3 className="font-medium text-[#1A1A1A]">{reg.name}</h3>
                              <div className="flex items-center gap-2">
                                <span className={`badge ${statusColors[reg.status] || 'badge-info'} capitalize`}>
                                  {reg.status}
                                </span>
                                <span className={`badge ${reg.payment_status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                                  {reg.payment_status === 'pago' ? 'Pago' : 'Pgto pendente'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mt-3">
                            <span className="flex items-center gap-1 text-[#73706A]">
                              <EnvelopeSimple size={14} /> {reg.email}
                            </span>
                            <span className="flex items-center gap-1 text-[#73706A]">
                              <Phone size={14} /> {reg.phone}
                            </span>
                            <span className="text-[#73706A] text-xs">
                              {new Date(reg.created_at).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {reg.categories?.map(cat => (
                              <span key={cat} className="badge badge-info text-xs">{getCatLabel(cat)}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(reg)} data-testid={`league-edit-${index}`}>
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setCurrentReg(reg); setDeleteDialogOpen(true); }}
                            className="text-[#9B2226] hover:bg-red-50"
                            data-testid={`league-delete-${index}`}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          )}

          {/* Duplas Admin Tab */}
          {isAdmin && (
            <TabsContent value="duplas-admin" className="mt-6 space-y-6" data-testid="duplas-admin-content">
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
                <h3 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <Users size={20} className="text-[#E85D04]" />
                  {duplaForm.id ? 'Editar dupla' : 'Adicionar nova dupla'}
                </h3>
                <form onSubmit={submitDupla} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Categoria *</Label>
                      <Select
                        value={duplaForm.category}
                        onValueChange={(v) => setDuplaForm({ ...duplaForm, category: v })}
                      >
                        <SelectTrigger data-testid="dupla-form-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {leagueCategories.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Observacoes</Label>
                      <Input
                        value={duplaForm.notes}
                        onChange={(e) => setDuplaForm({ ...duplaForm, notes: e.target.value })}
                        placeholder="Opcional"
                        data-testid="dupla-form-notes"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Jogador 1 *</Label>
                      <Input
                        value={duplaForm.player1}
                        onChange={(e) => setDuplaForm({ ...duplaForm, player1: e.target.value })}
                        placeholder="Nome completo"
                        required
                        data-testid="dupla-form-player1"
                      />
                    </div>
                    <div>
                      <Label>Jogador 2 *</Label>
                      <Input
                        value={duplaForm.player2}
                        onChange={(e) => setDuplaForm({ ...duplaForm, player2: e.target.value })}
                        placeholder="Nome completo"
                        required
                        data-testid="dupla-form-player2"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      disabled={duplaSaving}
                      className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
                      data-testid="dupla-form-save"
                    >
                      {duplaSaving ? 'Salvando...' : duplaForm.id ? 'Atualizar dupla' : 'Adicionar dupla'}
                    </Button>
                    {duplaForm.id && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDuplaForm({ id: null, category: duplaForm.category, player1: '', player2: '', notes: '' })}
                      >
                        Cancelar edicao
                      </Button>
                    )}
                  </div>
                </form>
              </div>

              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <h3 className="font-['Outfit'] font-semibold text-lg text-[#1A1A1A]">
                    Duplas confirmadas ({duplas.length})
                  </h3>
                  <Select value={duplasFilter} onValueChange={setDuplasFilter}>
                    <SelectTrigger className="w-56" data-testid="duplas-admin-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as categorias ({duplas.length})</SelectItem>
                      {leagueCategories.map(c => {
                        const n = duplas.filter(d => d.category === c.value).length;
                        return (
                          <SelectItem key={c.value} value={c.value}>{c.label} ({n})</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {duplas.length === 0 ? (
                  <div className="text-center py-12 text-[#73706A]">
                    <Users size={40} className="mx-auto mb-2 opacity-40" />
                    <p>Nenhuma dupla cadastrada ainda. Use o formulario acima.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(duplasFilter === 'all' ? duplas : duplas.filter(d => d.category === duplasFilter)).map((d, i) => (
                      <div
                        key={d.id}
                        className="flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-lg"
                        data-testid={`admin-dupla-row-${d.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#E85D04] uppercase font-semibold">
                            {getCatLabel(d.category)}
                          </p>
                          <p className="text-sm text-[#1A1A1A]">
                            <strong>{d.player1}</strong> + <strong>{d.player2}</strong>
                          </p>
                          {d.notes && <p className="text-[10px] text-[#73706A] truncate">{d.notes}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => editDupla(d)}
                          className="text-[#0077B6] hover:text-[#03045E]"
                          data-testid={`edit-dupla-${i}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDupla(d.id)}
                          className="text-[#9B2226] hover:text-[#9B2226]"
                          data-testid={`delete-dupla-${i}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Gerenciar Inscrição</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="league-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="confirmada">Confirmada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status do Pagamento</Label>
              <Select value={editForm.payment_status} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}>
                <SelectTrigger data-testid="league-edit-payment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observações</Label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="input-field min-h-[60px] resize-none"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#E85D04] hover:bg-[#DC2F02]" data-testid="league-edit-save">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-[#73706A] mt-2">Remover inscrição de <strong>{currentReg?.name}</strong>?</p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDelete} className="bg-[#9B2226] hover:bg-[#7d1c1f]" data-testid="league-confirm-delete">Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
